import { useMemo } from 'react';
import { Card, Statistic, Progress, Tag, Button, Descriptions } from 'antd';
import dayjs from 'dayjs';
import { woStatusLabel, woStatusColor, isCritical } from './workOrderHelpers';

function fmtDate(v) { const d = v ? dayjs(v) : null; return d && d.isValid() ? d.format('DD MMM YYYY') : '—'; }
function fmtDateTime(v) { const d = v ? dayjs(v) : null; return d && d.isValid() ? d.format('DD MMM YYYY, HH:mm') : '—'; }

// ════════════════════════════════════════════════════════════════════
// Stage WO-STATS — Work Order detail "Production overview" card.
//
// Maintainable layout: every block maps to a standard AntD primitive so a
// developer changes content, not layout math. Order facts are an AntD
// Descriptions (fields are a config array); the KPI strip is a grid of
// Statistic; the piece-state bar is DATA-DRIVEN (each segment's width is
// flex-grow = its count, so it follows the numbers with no hardcoded %);
// completion is a Progress circle; status/schedule are Tags. Scoped .wost-*
// CSS, no global theme-token overrides (rule 8).
//
// All figures derive from the detail payload (no extra request): pieces
// (state / scrap), lines (spec_snapshot dims), lineRfd, rejectedPieces. Area &
// weight use the same formulas as the printed Work Order copy (woCopyReport.js).
// ════════════════════════════════════════════════════════════════════

const KG_PER_M2_PER_MM = 2.5;
const SQFT_PER_SQM = 10.764;
const NF_AREA = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const NF_WEIGHT = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }

const CSS = `
.wost{margin-bottom:16px;
  --ink:#1f2733;--ink2:#5b667a;--ink3:#8c97a8;--line:#eceff3;
  --done:#16a34a;--wip:#1677ff;--queue:#d7dde6;--broken:#e5484d;--accent:#0e9c8e;--amber:#c2740f;--red:#dc2626;}
.wost .wost-card{border-color:#e8ebf0;overflow:hidden;}
.wost .wost-card .ant-card-body{padding:0;}

/* header */
.wost-head{display:flex;align-items:center;gap:11px;flex-wrap:wrap;padding:14px 18px;border-bottom:1px solid var(--line);}
.wost-wono{font-size:20px;font-weight:800;letter-spacing:-.01em;color:var(--ink);font-variant-numeric:tabular-nums;}
.wost-head .ant-tag{margin-inline-end:0;}
.wost-sp{margin-left:auto;}
.wost-pill{margin-inline-end:0;border:0;font-size:12px;font-weight:700;padding:2px 11px;border-radius:999px;
  display:inline-flex;align-items:center;gap:6px;line-height:1.6;}
.wost-pill::before{content:'';width:7px;height:7px;border-radius:50%;background:currentColor;}
.wost-pill.ok{color:#0B6B60;background:#D9F1EC;}
.wost-pill.warn{color:#9A5B00;background:#FBECD2;}
.wost-pill.bad{color:#B42318;background:#FBE1DE;}
.wost-pill.neu{color:#5B7583;background:#EDF2F4;}

/* body: completion + details */
.wost-body{display:flex;gap:22px;flex-wrap:wrap;padding:18px;border-bottom:1px solid var(--line);}
.wost-comp{flex:0 0 260px;display:flex;flex-direction:column;gap:13px;}
.wost-ringrow{display:flex;align-items:center;gap:14px;}
.wost-rtxt{font-variant-numeric:tabular-nums;font-weight:800;font-size:20px;color:var(--ink);letter-spacing:-.02em;}
.wost-rtxt i{font-size:10px;font-style:normal;color:var(--ink3);margin-left:1px;}
.wost-compmeta .big{font-size:15px;font-weight:750;color:var(--ink);font-variant-numeric:tabular-nums;}
.wost-compmeta .cap{font-size:11px;color:var(--ink3);text-transform:uppercase;letter-spacing:.05em;font-weight:700;}
.wost-bar{display:flex;height:12px;border-radius:6px;overflow:hidden;gap:2px;}
.wost-bar span{display:block;flex-basis:0;min-width:0;}
.wost-blegend{display:grid;grid-template-columns:1fr 1fr;gap:5px 14px;}
.wost-bl{display:flex;align-items:center;gap:7px;font-size:11.5px;color:var(--ink2);}
.wost-bl i{width:8px;height:8px;border-radius:2px;flex:0 0 auto;}
.wost-bl b{color:var(--ink);font-weight:700;font-variant-numeric:tabular-nums;margin-left:auto;}

/* details (AntD Descriptions) */
.wost-desc{flex:1 1 320px;min-width:280px;align-self:start;}
.wost-desc .ant-descriptions-item-label{width:1%;white-space:nowrap;}
.wost-due{display:inline-flex;align-items:center;font-size:11px;font-weight:700;color:#9a5b00;background:#fbeed6;
  border-radius:5px;padding:0 7px;line-height:1.7;margin-left:2px;}

/* KPI strip (grid of Statistic) */
.wost-stats{gap:1px;background:var(--line);}
.wost-stat{background:#fff;padding:11px 13px;}
.wost-stat .ant-statistic-title{font-size:11px;color:var(--ink2);margin-bottom:3px;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wost-sx{font-size:11.5px;color:var(--ink2);margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wost-sx.bad{color:var(--red);font-weight:600;}

/* narrow: stack completion above the details table so it gets full width */
@media (max-width:900px){
  .wost-body{flex-direction:column;}
  .wost-comp{flex:1 1 auto;}
}
`;

function Stat({ title, value, unit, sub, alarm }) {
  return (
    <div className="wost-stat">
      <Statistic
        title={title}
        value={value}
        suffix={unit || undefined}
        valueStyle={{ fontSize: 19, fontWeight: 700, color: '#1f2733', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}
      />
      {sub ? <div className={`wost-sx${alarm ? ' bad' : ''}`}>{sub}</div> : null}
    </div>
  );
}

export default function WorkOrderStatsPanel({ detail, onEditDelivery, canEditDelivery, onOpenProforma }) {
  const stats = useMemo(() => {
    const d = detail || {};
    const wo = d.workOrder || {};
    const lines = Array.isArray(d.lines) ? d.lines : [];
    const pieces = Array.isArray(d.pieces) ? d.pieces : [];
    const lineRfd = d.lineRfd && typeof d.lineRfd === 'object' ? d.lineRfd : {};

    // Ordered slots = original pieces (remake clones excluded). The status bar
    // breaks these ordered slots down by production state (sums to orderedTotal).
    const ordered = pieces.filter((p) => !p.scrappedFromId);
    const orderedTotal = ordered.length;
    const segCompleted = ordered.filter((p) => p.state === 'COMPLETED').length;
    const segInRoute = ordered.filter((p) => p.state === 'IN_ROUTE').length;
    const segNotStarted = ordered.filter((p) => p.state === 'NOT_STARTED').length;
    const segBroken = ordered.filter((p) => p.state === 'SCRAPPED').length;
    const piecePct = orderedTotal > 0 ? Math.round((segCompleted / orderedTotal) * 100) : 0;

    // KPI-scoped counts: all pieces on the floor (incl. remake clones).
    const inProgress = pieces.filter((p) => p.state === 'IN_ROUTE').length;
    const notStarted = pieces.filter((p) => p.state === 'NOT_STARTED').length;
    const broken = pieces.filter((p) => p.state === 'SCRAPPED').length;
    const remade = pieces.filter((p) => p.scrappedFromId).length;
    const rejected = num((detail || {}).rejectedPieces);
    // Breakage rate = broken / all glass physically cut (every piece row).
    const cutGlass = pieces.length;
    const breakageRate = cutGlass > 0 ? Math.round((broken / cutGlass) * 100) : 0;

    let rfdDone = 0; let rfdRouted = 0;
    Object.values(lineRfd).forEach((v) => { rfdDone += num(v && v.done); rfdRouted += num(v && v.routed); });
    const rfdPct = rfdRouted > 0 ? Math.round((rfdDone / rfdRouted) * 100) : 0;

    let areaSqm = 0; let weightKg = 0;
    lines.forEach((l) => {
      const sp = l.specSnapshot || {};
      const qty = num(l.requiredQty) || num(sp.qty);
      const a = (num(sp.widthMm) * num(sp.heightMm) / 1e6) * qty;
      areaSqm += a;
      weightKg += a * num(sp.thicknessMm) * KG_PER_M2_PER_MM;
    });
    const areaSqft = areaSqm * SQFT_PER_SQM;

    const rel = wo.releasedAt ? dayjs(wo.releasedAt) : null;
    const days = rel && rel.isValid() ? Math.max(0, dayjs().startOf('day').diff(rel.startOf('day'), 'day')) : null;
    const critical = days != null && isCritical(days, wo.status);
    const dlv = wo.deliveryDate ? dayjs(wo.deliveryDate) : null;
    const daysToDelivery = dlv && dlv.isValid() ? dlv.startOf('day').diff(dayjs().startOf('day'), 'day') : null;

    return {
      wo, orderedTotal, segCompleted, segInRoute, segNotStarted, segBroken, piecePct,
      inProgress, notStarted, broken, remade, rejected, breakageRate,
      rfdDone, rfdRouted, rfdPct, rfdInRoute: rfdRouted > 0,
      areaSqm, areaSqft, weightKg,
      days, critical, dlv, daysToDelivery,
      completed: wo.status === 'COMPLETED'
    };
  }, [detail]);

  const s = stats;
  const wo = s.wo;
  const ringColor = s.piecePct === 100 ? '#16A34A' : '#0E9C8E';

  // Schedule verdict pill (top-right) + its accent colour, reused in the clock row.
  let pill = { cls: 'neu', text: 'No delivery date' };
  if (s.completed) pill = { cls: 'ok', text: 'Production complete' };
  else if (s.dlv && s.dlv.isValid()) {
    const n = s.daysToDelivery;
    if (n < 0) pill = { cls: 'bad', text: `Overdue ${Math.abs(n)}d` };
    else if (n === 0) pill = { cls: 'warn', text: 'Due today' };
    else if (n <= 3) pill = { cls: 'warn', text: `Due in ${n}d` };
    else pill = { cls: 'ok', text: `On track · ${n}d left` };
  }
  const clockColor = { ok: '#0a7d76', warn: '#c2740f', bad: '#dc2626', neu: '#8c97a8' }[pill.cls];

  const dueTag = wo.deliveryDate ? `${fmtDate(wo.deliveryDate)}` : null;
  const pillText = s.completed && s.dlv ? 'Production complete' : pill.text;

  // Data-driven piece-state segments (width follows the count via flex-grow).
  const segs = [
    { key: 'done', label: 'Completed', count: s.segCompleted, color: '#16a34a' },
    { key: 'wip', label: 'In progress', count: s.segInRoute, color: '#1677ff' },
    { key: 'queue', label: 'Queued', count: s.segNotStarted, color: '#d7dde6' },
    { key: 'broken', label: 'Broken', count: s.segBroken, color: '#e5484d' },
  ];

  const tiles = [
    { key: 'inprog', title: 'In progress', value: s.inProgress, unit: 'pcs',
      sub: s.notStarted > 0 ? `${s.notStarted} queued` : 'all on floor' },
    { key: 'ready', title: 'Ready to dispatch', value: s.rfdInRoute ? s.rfdDone : '—',
      unit: s.rfdInRoute ? `/ ${s.rfdRouted}` : '',
      sub: s.rfdInRoute ? `${s.rfdPct}% cleared` : 'no dispatch stage' },
    { key: 'disp', title: 'Dispatched', value: '—', unit: 'pcs', sub: '— m² delivered' },
    { key: 'break', title: 'Breakage', value: s.broken, unit: 'pcs', alarm: s.broken > 0,
      sub: s.broken > 0 ? `${s.breakageRate}% · ${s.remade} remade` : 'none broken' },
    { key: 'reject', title: 'Rejection', value: s.rejected, unit: 'pcs', alarm: s.rejected > 0,
      sub: s.rejected > 0 ? 'rework rejected' : 'none rejected' },
    { key: 'area', title: 'Glass area', value: NF_AREA.format(s.areaSqm), unit: 'm²',
      sub: `${NF_AREA.format(s.areaSqft)} ft²` },
    { key: 'weight', title: 'Glass weight', value: NF_WEIGHT.format(s.weightKg), unit: 'kg',
      sub: `across ${s.orderedTotal} pcs` },
  ];

  const proformaValue = wo.proformaNo
    ? (onOpenProforma
      ? <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 13 }} onClick={onOpenProforma}>{wo.proformaNo}</Button>
      : wo.proformaNo)
    : '—';
  const deliveryValue = (
    <span>{fmtDate(wo.deliveryDate)}
      {canEditDelivery && onEditDelivery
        ? <Button type="link" size="small" style={{ padding: '0 0 0 6px', height: 'auto', fontSize: 12 }} onClick={onEditDelivery}>{wo.deliveryDate ? 'Edit' : 'Set'}</Button>
        : null}
    </span>
  );
  const clockValue = s.days != null
    ? <span>Day {s.days}{pillText ? <> · <span style={{ color: clockColor, fontWeight: 600 }}>{pillText}</span></> : null}</span>
    : '—';

  return (
    <div className="wost">
      <style>{CSS}</style>

      <Card size="small" className="wost-card" variant="outlined">
        {/* header — identity + schedule verdict */}
        <div className="wost-head">
          <span className="wost-wono">{wo.woNo || '—'}</span>
          {wo.status ? (
            <Tag color={woStatusColor(wo.status)} style={{ marginInlineEnd: 0, fontSize: 12, fontWeight: 700, padding: '2px 10px', lineHeight: 1.5 }}>
              {(woStatusLabel(wo.status) || '').toUpperCase()}
            </Tag>
          ) : null}
          <span className="wost-sp" />
          <Tag className={`wost-pill ${pill.cls}`} bordered={false}>
            {pill.text}{dueTag && pill.cls !== 'neu' ? ` · ${dueTag}` : ''}
          </Tag>
        </div>

        {/* body — completion + piece-state bar | order details */}
        <div className="wost-body">
          <div className="wost-comp">
            <div className="wost-ringrow">
              <Progress type="circle" percent={s.piecePct} size={78} strokeWidth={8}
                strokeColor={ringColor} trailColor="#EEF1F5"
                format={(p) => <span className="wost-rtxt">{p}<i>%</i></span>} />
              <div className="wost-compmeta">
                <div className="big">{s.segCompleted} / {s.orderedTotal}</div>
                <div className="cap">pieces produced</div>
              </div>
            </div>
            <div className="wost-bar">
              {segs.map((g) => (
                <span key={g.key} style={{ flexGrow: g.count, background: g.color }} title={`${g.label}: ${g.count}`} />
              ))}
            </div>
            <div className="wost-blegend">
              {segs.map((g) => (
                <span key={g.key} className="wost-bl"><i style={{ background: g.color }} />{g.label} <b>{g.count}</b></span>
              ))}
            </div>
          </div>

          <Descriptions className="wost-desc" bordered size="small" column={{ xs: 1, sm: 2, md: 2 }}>
            <Descriptions.Item label="Customer" span={2}>
              <span title={wo.customerName || ''}>{wo.customerName || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Proforma">{proformaValue}</Descriptions.Item>
            <Descriptions.Item label="Site">{wo.siteCode ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Released">{fmtDateTime(wo.releasedAt)}</Descriptions.Item>
            <Descriptions.Item label="Delivery">{deliveryValue}</Descriptions.Item>
            <Descriptions.Item label="In production">{clockValue}</Descriptions.Item>
            <Descriptions.Item label="Completed">{fmtDateTime(wo.completedAt)}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* KPI strip */}
        <div className="wost-stats grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
          {tiles.map((t) => (
            <Stat key={t.key} title={t.title} value={t.value} unit={t.unit} sub={t.sub} alarm={t.alarm} />
          ))}
        </div>
      </Card>
    </div>
  );
}
